import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface RequireRoleProps {
  roles: AppRole[];
  children: ReactNode;
  /** Where to send unauthenticated visitors. Defaults to /auth */
  unauthenticatedRedirect?: string;
  /** Where to send authenticated-but-unauthorized visitors. Defaults to / */
  forbiddenRedirect?: string;
}

/**
 * Server-side role enforcement happens via RLS + SECURITY DEFINER RPCs.
 * This component prevents the protected page from rendering, mounting, or
 * firing queries until the user's roles are confirmed. Any role mismatch
 * triggers an immediate redirect, so unauthorized users never see the
 * admin/exec UI shell or trigger its data hooks.
 */
export default function RequireRole({
  roles,
  children,
  unauthenticatedRedirect = "/auth",
  forbiddenRedirect = "/",
}: RequireRoleProps) {
  const { user, loading: authLoading } = useAuth();
  const { data: userRoles, isLoading: rolesLoading } = useUserRoles();

  if (authLoading || (user && rolesLoading)) {
    return null;
  }

  if (!user) {
    return <Navigate to={unauthenticatedRedirect} replace />;
  }

  const allowed = (userRoles ?? []).some((r) => roles.includes(r as AppRole));
  if (!allowed) {
    return <Navigate to={forbiddenRedirect} replace />;
  }

  return <>{children}</>;
}
